import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { useDocumentTitle } from 'usehooks-ts'
import { Main } from '@/components/main'
import SettingSideBarNav from '@/pages/settings/components/SettingSidebar'
import { useMemo, useEffect } from 'react'
import { trackEvent } from '@/lib/posthog'
import { useLocation } from '@tanstack/react-router'
import { APP_NAME } from '@/lib/env'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { RequireAdmin } from '@/components/permissions'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useOrganizationPermissions } from '@/hooks/useOrganizationPermissions'
import { useSubscription } from '@/hooks/useSubscription'
import PageLoading from '@/components/PageLoading'
import ForbiddenError from '@/pages/errors/forbidden'
// import { GlydeTopBar } from '@/components/glyde-top-bar'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAtom } from 'jotai'
import { activeOrganizationWithStorageAtom, activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'

export const Route = createFileRoute(
  '/(protected)/settings'
)({
  component: SettingsLayout,
})

// Routes that don't require admin access (profile + members view)
// Note: Security is admin-only because MFA is required for admin access
const PUBLIC_SETTINGS_ROUTES = ['/settings/profile', '/settings/organization', '/settings/security', '/settings/writing']

function SettingsLayout() {
  useDocumentTitle(`Settings - ${APP_NAME}`)
  const location = useLocation()
  const [activeOrganization] = useAtom(activeOrganizationWithStorageAtom)
  const [activeWorkspace] = useAtom(activeWorkspaceWithStorageAtom)
  const workspaceId = activeWorkspace?.id
  const routerState = useRouterState()
  const { enabled: apiKeysEnabled } = useFeatureFlag('apiKeys')
  const { enabled: templatesEnabled } = useFeatureFlag('templates')
  const { isAdmin, isLoading: isLoadingPermissions } = useWorkspacePermissions(workspaceId)
  const {
    canManageBilling,
    isLoading: isLoadingBillingPermissions,
  } = useOrganizationPermissions(activeOrganization?.id)
  const { subscription } = useSubscription()
  const isEnterprise = subscription?.plan === 'ENTERPRISE'
  const nextPath = routerState.location.pathname
// 或 nextHref = routerState.location.href



  const isNavigatingAway =
    routerState.status === 'pending' &&
    !(routerState.pendingMatches || []).some((m) => m.routeId === Route.id) &&!nextPath.includes('/settings')


  // Check if current route is a public settings page (doesn't require admin)
  const isPublicSettingsRoute = PUBLIC_SETTINGS_ROUTES.some(route =>
    location.pathname.includes(route)
  )

  // Filter sidebar items based on plan and user role
  const filteredSidebarItems = useMemo(() => {
    let items = [...sidebarNavItems];

    // Non-admin users see Organization, Members, Profile, and Security
    if (!isAdmin) {
      items = items.filter(item =>
        ['Organization', 'Profile', 'Security', 'Writing'].includes(item.title)
      )
      return items
    }

    if (!canManageBilling) {
      items = items.filter(item => item.title !== 'Billing')
    }

    // Filter by plan/feature flags
    if (!apiKeysEnabled) {
      items = items.filter(item => item.title !== 'API Keys');
    }

    // Doc Templates gated behind the `templates` feature flag
    if (!templatesEnabled) {
      items = items.filter(item => item.href !== '/settings/doc-templates');
    }

    // Signing settings only for Enterprise plan (RFC-132 BYOK)
    if (!isEnterprise) {
      items = items.filter(item => item.title !== 'Signing');
    }

    return items;
  }, [apiKeysEnabled, templatesEnabled, isAdmin, isEnterprise, canManageBilling])

  // Track settings page views
  useEffect(() => {
    // Extract page name from pathname (e.g., /settings/members -> members)
    const pathParts = location.pathname.split('/')
    const settingsIndex = pathParts.indexOf('settings')
    const pageName = pathParts[settingsIndex + 1] || 'general'

    // Map page names to valid enum values
    const validPages = ['general', 'branding', 'publishing', 'properties', 'writing', 'templates', 'doc-templates', 'mcp-server', 'organization', 'members', 'api-keys', 'billing', 'profile', 'signing']
    const page = validPages.includes(pageName) ? pageName : 'general'

    trackEvent('settings.page.viewed', {
      page,
      workspace_id: workspaceId,
    })
  }, [location.pathname, workspaceId])

  // Extract current settings page for audit logging
  const currentSettingsPage = useMemo(() => {
    const pathParts = location.pathname.split('/')
    const settingsIndex = pathParts.indexOf('settings')
    return pathParts[settingsIndex + 1] || 'general'
  }, [location.pathname])
  const isBillingRoute = currentSettingsPage === 'billing'

  const settingsContent = (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="h-screen overflow-hidden">
        <Main  className='h-full overflow-hidden bg-muted '>
          <ScrollArea className="h-screen" viewClassName="[&>div]:!block p-8">
          {/* <GlydeTopBar /> */}
          <div className='mx-auto flex w-full max-w-[1200px] flex-1 flex-col overflow-hidden'>
            {/* Settings header */}
            <div className='mb-8'>
              <h1 className='font-[Fraunces] text-[40px] font-medium leading-tight tracking-tight text-sidebar-foreground'>
                Settings
              </h1>
              <p className='mt-1 text-sm text-muted-foreground'>
                Manage your workspace and account preferences.
              </p>
            </div>
            {/* Two-column: nav + content */}
            <div className='flex flex-1 flex-col gap-10 overflow-hidden lg:flex-row'>
              <aside className='lg:w-[200px] w-full shrink-0'>
                <SettingSideBarNav items={filteredSidebarItems} />
              </aside>
              <div className='flex flex-1 justify-center overflow-hidden'>
                <div className='h-full w-full overflow-hidden'>
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
          </ScrollArea>
        </Main>
      </SidebarInset>
    </SidebarProvider>
  )

  // Show loading state while permissions are being fetched
  if (isBillingRoute && isLoadingBillingPermissions) {
    return <PageLoading />
  }

  if (isLoadingPermissions) {
    return settingsContent
  }

  // 离开 Settings 时，如果新路由还在 pending，先显示 Loading，避免闪 403
  if (isNavigatingAway) {
    return <PageLoading />
  }

  if (isBillingRoute && !canManageBilling) {
    return <ForbiddenError />
  }

  // Public settings routes (profile, members) are accessible to all users
  if (isPublicSettingsRoute) {
    return settingsContent
  }

  // Admin-only routes require admin access - non-admins will see 403
  return (
    <RequireAdmin resource={`settings/${currentSettingsPage}`}>
      {settingsContent}
    </RequireAdmin>
  )
}


const sidebarNavItems = [
  {
    title: 'Organization',
    href: '/settings/organization',
    label: 'Organization',
  },
  // {
  //   title: 'General',
  //   href: '/settings/general',
  //   label: 'Workspace',
  // },
  // {
  //   title: 'Publishing',
  //   href: '/settings/publishing',
  //   label: 'Workspace',
  // },
  // {
  //   title: 'Properties',
  //   href: '/settings/properties',
  //   label: 'Workspace',
  // },
  {
    title: 'Writing',
    href: '/settings/writing',
    label: 'Workspace',
  },
  {
    title: 'Templates',
    href: '/settings/doc-templates',
    label: 'Workspace',
  },
  // {
  //   title: 'Branding',
  //   href: '/settings/branding',
  //   label: 'Workspace',
  // },
  // {
  //   title: 'MCP Server',
  //   href: '/settings/mcp-server',
  //   label: 'Workspace',
  // },
  // {
  //   title: 'Members',
  //   href: '/settings/members',
  //   label: 'Organization',
  // },
  {
    title: 'API Keys',
    href: '/settings/api-keys',
    label: 'Workspace',
  },
  {
    title: 'Billing',
    href: '/settings/billing',
    label: 'Organization',
  },
  {
    title: 'Signing',
    href: '/settings/signing',
    label: 'Organization',
  },
  {
    title: 'Profile',
    href: '/settings/profile',
    label: 'Account',
  },
  {
    title: 'Security',
    href: '/settings/security',
    label: 'Account',
  },
  // {
  //   title: 'Audit Logs',
  //   href: '/settings/audit-logs',
  //   label: 'Organization',
  // },
]
